import { Mutation } from "@apollo/client/react/components";
import React from "react";
import * as palette from "@govlaunch/palette";
import SetProfileCover from "~/lib/mutations/SetProfileCover";
import Cover, { makeImaginaryUrls, PLACEHOLDER_COVER } from "~/components/cover/Cover";
import getOr from "lodash/fp/getOr";
import ProfilePicture from "@govlaunch/profile-picture";
import { Field, Form } from "react-final-form";
import { Filestack, Button } from "@govlaunch/core";
import FieldValidationError from "~/components/form/FieldValidationError";
import { Margin } from "~/components/spacings";
import { IGovernmentQuery } from "~/lib/queries/__generated__/GovernmentQuery.generated";
import getCurrencySignal from "~/components/government/utils/getCurrencySignal";
import * as Yup from "yup";
import validate from "~/lib/utils/validate";
import UpdateGovernmentProfile from "~/lib/mutations/UpdateGovernmentProfile";
import {
  IUpdateGovernmentProfileMutation,
  IUpdateGovernmentProfileMutationVariables,
} from "~/lib/mutations/__generated__/UpdateGovernmentProfile.generated";
import { ISelfieQuery } from "~/lib/queries/__generated__/SelfieQuery.generated";
import SelfieQuery from "~/lib/queries/SelfieQuery";
import { FormattedMessage, useIntl } from "react-intl";

interface IGovernmentProfileFormProps {
  government: Pick<
    NonNullable<IGovernmentQuery["government"]>,
    "_id" | "slug" | "logo" | "site" | "name" | "budget" | "currency" | "cover" | "city"
  >;
}

const validationSchema = Yup.object().shape({
  site: Yup.string().nullable().url("Invalid URL").trim(),
  budget: Yup.number().nullable(),
  population: Yup.number().nullable(),
});

export default function GovernmentProfileForm({ government }: IGovernmentProfileFormProps) {
  const intl = useIntl();

  return (
    <Mutation<IUpdateGovernmentProfileMutation, IUpdateGovernmentProfileMutationVariables>
      mutation={UpdateGovernmentProfile}
      refetchQueries={["Government"]}
      update={(proxy, { data }) => {
        if (!data || !data.updateGovernmentProfile) {
          return;
        }

        const showCompleteGovernmentProfileBanner = !(
          data.updateGovernmentProfile.logo &&
          data.updateGovernmentProfile.budget &&
          data.updateGovernmentProfile.site
        );

        const selfieQuery = proxy.readQuery<ISelfieQuery>({
          query: SelfieQuery,
          variables: {
            token: null,
          },
        });

        if (!selfieQuery || !selfieQuery.selfie || selfieQuery.selfie.__typename !== "GovernmentUser") {
          return;
        }

        proxy.writeQuery<ISelfieQuery>({
          query: SelfieQuery,
          data: {
            __typename: "Query",
            selfie: {
              ...selfieQuery.selfie,
              showCompleteGovernmentProfileBanner,
            },
          },
        });
      }}
    >
      {(updateGovernmentProfile) => (
        <Form
          initialValues={{
            logo: government.logo,
            site: government.site,
            budget: government.budget,
            population: government.city.population,
          }}
          validate={validate(validationSchema)}
          subscription={{
            submitting: true,
            invalid: true,
          }}
          onSubmit={(values) => {
            return updateGovernmentProfile({
              variables: {
                governmentId: government._id,
                government: {
                  site: values.site || null,
                  budget: values.budget || null,
                  logo: values.logo || null,
                  population: values.population || null,
                },
              },
            });
          }}
        >
          {({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              <div
                css={{
                  position: "relative",
                }}
              >
                <SetProfileCover entityType="Government" targetId={government._id}>
                  {({ onSetProfileCover, onSetRepositionedCover, onRemoveCover }) => (
                    <Cover
                      alt="Cover"
                      height={250}
                      originalSource={getOr(null, "cover.original", government)}
                      repositionedSource={getOr(null, "cover.repositioned", government)}
                      placeholderSource={PLACEHOLDER_COVER}
                      onReposition={(originalSource, cropState) => {
                        const repositionedSource = makeImaginaryUrls(originalSource, cropState);
                        onSetRepositionedCover(originalSource, repositionedSource.cropped, cropState);
                      }}
                      onChange={onSetProfileCover}
                      onRemove={onRemoveCover}
                      readOnly={false}
                      containerProps={{
                        style: {
                          borderRadius: 5,
                          overflow: "hidden",
                        },
                      }}
                    />
                  )}
                </SetProfileCover>

                <div
                  css={{
                    position: "absolute",
                    top: "50%",
                    marginTop: -30,
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <Field name="logo">
                    {({ input }) => (
                      <Filestack
                        onSuccess={({ filesUploaded }: any) => input.onChange(filesUploaded[0].url)}
                        customRender={({ onPick }: any) => (
                          <div>
                            <div
                              css={{
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              <ProfilePicture xlarge={true} image={input.value} name={government.name || "A B"} />
                            </div>

                            <div
                              css={{
                                display: "grid",
                                gridAutoFlow: "column",
                                gridAutoColumns: "max-content",
                                justifyContent: "center",
                                gridColumnGap: 5,
                                marginTop: 16,
                              }}
                            >
                              <Button size="xsmall" color={palette.primary} onClick={onPick}>
                                <FormattedMessage defaultMessage="Upload" id="p4N05H" />
                              </Button>

                              {input.value && (
                                <Button
                                  size="xsmall"
                                  color={palette.red}
                                  theme="secondary"
                                  css={{
                                    background: "#fff",
                                  }}
                                  onClick={() => input.onChange(null)}
                                >
                                  <FormattedMessage defaultMessage="Remove" id="G/yZLu" />
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      />
                    )}
                  </Field>
                </div>
              </div>

              <div
                css={{
                  border: `1px solid ${palette.lightestGray}`,
                  borderRadius: 4,
                  marginTop: 24,
                  background: "#fff",
                }}
              >
                <div
                  css={{
                    borderBottom: `1px solid ${palette.lightestGray}`,
                    padding: 16,
                    fontSize: 14,
                    fontWeight: 500,
                    color: palette.sealBlue,
                  }}
                >
                  <FormattedMessage defaultMessage="Profile" id="itPgxd" />
                </div>
                <div
                  css={{
                    padding: 16,
                    display: "grid",
                    gridRowGap: 16,
                  }}
                >
                  <Field name="site">
                    {({ input, meta }) => (
                      <div>
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <label
                            css={{
                              fontWeight: 500,
                              fontSize: 14,
                              color: palette.mediumGray,
                              margin: 0,
                            }}
                          >
                            <FormattedMessage defaultMessage="Website" id="JkLHGw" />
                          </label>

                          <FieldValidationError meta={meta} />
                        </div>

                        <Margin mt={12}>
                          <input
                            placeholder={intl.formatMessage({
                              defaultMessage: "Type government website",
                              id: "ulRgSs",
                            })}
                            maxLength={80}
                            css={{
                              width: "100%",
                              height: 42,
                              padding: "6px 12px",
                              fontSize: 14,
                              border: `solid 1px ${palette.lightestGray}`,
                              borderRadius: 6,
                              outline: 0,
                              ":focus": {
                                borderColor: palette.primary,
                              },
                              "::placeholder": {
                                color: palette.sealBlue,
                              },
                            }}
                            {...input}
                          />
                        </Margin>
                      </div>
                    )}
                  </Field>

                  <Field
                    name="budget"
                    format={(value) =>
                      `${getCurrencySignal(government.currency)} ${value || 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parse={(value) => {
                      return value.toString().replace(/([^\d]+)/g, "").length > 0
                        ? parseInt(value.replace(/([^\d]+)/g, ""))
                        : 0;
                    }}
                  >
                    {({ input, meta }) => (
                      <div>
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <label
                            css={{
                              fontWeight: 500,
                              fontSize: 14,
                              color: palette.mediumGray,
                              margin: 0,
                            }}
                          >
                            <FormattedMessage defaultMessage="Budget" id="0KKXrH" />
                          </label>

                          <FieldValidationError meta={meta} />
                        </div>

                        <Margin mt={12}>
                          <input
                            placeholder={intl.formatMessage({
                              defaultMessage: `What's the government budget?`,
                              id: "/ME8C4",
                            })}
                            maxLength={80}
                            css={{
                              width: "100%",
                              height: 42,
                              padding: "6px 12px",
                              fontSize: 14,
                              border: `solid 1px ${palette.lightestGray}`,
                              borderRadius: 6,
                              outline: 0,
                              ":focus": {
                                borderColor: palette.primary,
                              },
                              "::placeholder": {
                                color: palette.sealBlue,
                              },
                            }}
                            {...input}
                          />
                        </Margin>
                      </div>
                    )}
                  </Field>

                  <Field
                    name="population"
                    format={(value) => `${value || ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parse={(value) => {
                      return value.toString().replace(/([^\d]+)/g, "").length > 0
                        ? parseInt(value.replace(/([^\d]+)/g, ""))
                        : 0;
                    }}
                  >
                    {({ input, meta }) => (
                      <div>
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <label
                            css={{
                              fontWeight: 500,
                              fontSize: 14,
                              color: palette.mediumGray,
                              margin: 0,
                            }}
                          >
                            <FormattedMessage defaultMessage="Population" id="+EKK8l" />
                          </label>

                          <FieldValidationError meta={meta} />
                        </div>

                        <Margin mt={12}>
                          <input
                            placeholder={intl.formatMessage({
                              defaultMessage: `What's the government population?`,
                              id: "9sAygw",
                            })}
                            maxLength={80}
                            css={{
                              width: "100%",
                              height: 42,
                              padding: "6px 12px",
                              fontSize: 14,
                              border: `solid 1px ${palette.lightestGray}`,
                              borderRadius: 6,
                              outline: 0,
                              ":focus": {
                                borderColor: palette.primary,
                              },
                              "::placeholder": {
                                color: palette.sealBlue,
                              },
                            }}
                            {...input}
                          />
                        </Margin>
                      </div>
                    )}
                  </Field>

                  <div
                    css={{
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button type="submit" disabled={submitting}>
                      <FormattedMessage defaultMessage="Save" id="jvo0vs" />
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Form>
      )}
    </Mutation>
  );
}
